import { uiPaper } from "shared/ui/uiSurface"
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useCreateLinkMutation } from "../../apis/appApi"

type Props = {
  open: boolean
  onClose: () => void
}

export const CreateLinkDialog: FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation()
  const [createLink, { isLoading }] = useCreateLinkMutation()
  const [haulerCode, setHaulerCode] = useState("")
  const [error, setError] = useState("")

  const handleClose = useCallback(() => {
    setHaulerCode("")
    setError("")
    onClose()
  }, [onClose])

  const handleSubmit = useCallback(async () => {
    if (!haulerCode.trim()) return
    try {
      setError("")
      await createLink({ haulerCode: haulerCode.trim() }).unwrap()
      handleClose()
    } catch (err: any) {
      const msg = err?.data?.message || err?.data?.error || "Failed to create link"
      setError(msg)
    }
  }, [haulerCode, createLink, handleClose])

  return (
    <Dialog
      slotProps={{ paper: uiPaper("UI-DLV2-136") }}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="create-link-dialog"
    >
      <DialogTitle>{t("courierLinks.create.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("courierLinks.create.haulerCode")}
            value={haulerCode}
            onChange={(e) => setHaulerCode(e.target.value)}
            required
            fullWidth
            size="small"
            autoFocus
            helperText={t("courierLinks.create.haulerCodeHelper")}
            data-testid="create-link-hauler-code-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && haulerCode.trim() && !isLoading) {
                handleSubmit()
              }
            }}
          />
          {error && (
            <Alert severity="error" data-testid="create-link-error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-testid="create-link-cancel-btn">
          {t("courierLinks.create.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!haulerCode.trim() || isLoading}
          data-testid="create-link-submit-btn"
        >
          {t("courierLinks.create.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
